import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { JpaQueries, JPA_QUERY_KEYS } from '@api/services/jpa';
import { NprMutations } from '@api/services/npr';
import { useJpa, useRequest, useModal, usePlss } from '@stores';
import { useExistingProjects, useProjectLocation } from '@api/services/project/useProjects';
import { PROJECT_QUERY_KEYS } from '@api/services/project';
import { connect } from 'redux-bundler-react';

import { ModalContent, ModalTabContainerNewRequest } from '@components/modal';
import ProjectConfirmationTab from './modal-tabs/ProjectConfirmationTab';
import ProjectLocationTab from './modal-tabs/project-location-tab/ProjectLocationTab';
import RequestTypes from '../new-request/RequestTypes';

import '@pages/Home/home.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { RequestType, ErrorMessages, RequestTypeStringsLookup, RequestTypeStringIds } from '@src/utils/enums';
import { latRegex, lngRegex, cityRegex, zipCodeRegex, decimalNumberRegex } from '@src/utils/regex';
import { formatCoordFlt } from '@src/utils/helpers';
import { DASHBOARD_QUERY_KEYS } from '@api/services/dashboard';
import { getBaseRequestValues } from '@api/helpers';
import { buildPreviousFileNumbers } from '@forms/input-forms/helpers';
import { useAddNonPcnMutation } from '@api/services/non-pcn/hooks/useAddNonPcnMutation';
import { prefetchCreditUnitsByDistrict } from '@src/api/services/lookup/prefetch';
import { useGetDistrictLocationFromGeometry } from '@api/services/aor/hooks';
import { useAllowedRequestTypesForLocation } from '@src/api/services/project/hooks';
import { useNavigate } from '@tanstack/react-router';

const defaultValues = {
  country: 'US',
  existingProjectId: null,
  hasExistingProject: null,
  requestType: [],
  showAddressInput: false,
  showGDBInput: false,
  showMapInput: false,
};

const NewApplicationModal = connect(
  'doAddJD',
  'doAddPermit',
  'doAddPreApp',
  'doFetchGPPermitsByDistrict',
  'doResetReduxFormData',
  'doUpdateJDRequest',
  'doUpdateRequestData',
  'selectRequestFormDataLocation',
  ({
    daNumber,
    doAddJD,
    doAddPermit,
    doAddPreApp,
    doFetchGPPermitsByDistrict,
    doResetReduxFormData,
    doUpdateJDRequest,
    doUpdateRequestData,
    fromExistingProject,
    projectId,
    projectName,
    requestFormDataLocation,
  }) => {
    const queryClient = useQueryClient();

    const { closeModal } = useModal();
    const { setJpaOrgIds, jpaOrgIds } = useJpa();
    const { projectGeometry, resetRequest, setIsNewRequest, setSelectedRequestId } = useRequest();
    const { mutate: addNonPcn } = useAddNonPcnMutation();
    const { plssData } = usePlss();
    const navigate = useNavigate();

    const [projectOutsideUS, setProjectOutsideUS] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const districtLocations = useGetDistrictLocationFromGeometry(requestFormDataLocation?.geometry);
    const district = requestFormDataLocation?.district ?? districtLocations?.[0]?.district;

    const schema = useMemo(
      () =>
        yup
          .object()
          .shape({
            hasExistingProject: yup.boolean().required(),

            existingProjectId: yup
              .mixed()
              .nullable()
              .when('hasExistingProject', {
                is: true,
                then: () =>
                  yup
                    .object({
                      label: yup.string().required(),
                      value: yup.mixed().required(),
                    })
                    .required('Please select an option'),
                otherwise: () => yup.mixed().notRequired(),
              }),

            projectName: yup.string().when('hasExistingProject', {
              is: false,
              then: (schema) => schema.required('Field is required'),
              otherwise: (schema) => schema.notRequired(),
            }),

            hasPreviousFileNumber: yup.string().when('hasExistingProject', {
              is: false,
              then: (schema) => schema.required('Please select an option'),
              otherwise: (schema) => schema.notRequired(),
            }),

            previousFileNumbers: yup.array().when('hasPreviousFileNumber', {
              is: 'true',
              then: () =>
                yup
                  .array()
                  .min(1, 'At least one Previous File Number is required')
                  .required('At least one Previous File Number is required'),
              otherwise: () =>
                yup.array().when('hasPreviousFileNumber', {
                  is: 'false',
                  then: () => yup.array().nullable(),
                }),
            }),

            showAddressInput: yup.boolean().nullable(),
            showMapInput: yup.boolean().nullable(),
            showGDBInput: yup.boolean().nullable(),

            addressInputType: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'showAddressInput'], {
                is: (hasExistingProject, show) => !hasExistingProject && show === true,
                then: () => yup.string().required('Please select an option'),
              }),

            address: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'addressInputType', 'showAddressInput'], {
                is: (hasExistingProject, type, show) =>
                  !hasExistingProject && type?.includes('address') && show === true,
                then: () => yup.string().required('Field is required'),
              }),

            addressTwo: yup.string().nullable(),

            city: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'addressInputType', 'showAddressInput'], {
                is: (hasExistingProject, type, show) =>
                  !hasExistingProject && type?.includes('address') && show === true,
                then: () =>
                  yup
                    .string()
                    .matches(cityRegex, { message: 'Field is invalid', excludeEmptyString: true })
                    .required('Field is required'),
              }),

            state: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'addressInputType', 'showAddressInput'], {
                is: (hasExistingProject, type, show) =>
                  !hasExistingProject && type?.includes('address') && show === true,
                then: () => yup.string().required('Field is required'),
              }),

            zipcode: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'addressInputType', 'showAddressInput'], {
                is: (hasExistingProject, type, show) =>
                  !hasExistingProject && type?.includes('address') && show === true,
                then: () =>
                  yup
                    .string()
                    .matches(zipCodeRegex, { message: 'Field is invalid', excludeEmptyString: true })
                    .required('Field is required'),
              }),

            county: yup
              .string()
              .nullable()
              .matches(cityRegex, { message: 'Field is invalid', excludeEmptyString: true }),

            country: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'addressInputType', 'showAddressInput'], {
                is: (hasExistingProject, type, show) =>
                  !hasExistingProject && type?.includes('address') && show === true,
                then: () => yup.string().required('Field is required'),
              }),

            latitude: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'addressInputType', 'showAddressInput'], {
                is: (hasExistingProject, type, show) =>
                  !hasExistingProject && type?.includes('coordinates') && show === true,
                then: () =>
                  yup
                    .string()
                    .required('Field is required')
                    .test('latInRange', 'Latitude must be between 0 and 90', (val) =>
                      !projectOutsideUS ? Number(val) <= 90 && Number(val) >= 0 : true
                    )
                    .test(
                      'latFormat',
                      'Latitude format is incorrect. Must be +-XX.XXXXXX and include at least 6 decimal places.',
                      (val) => latRegex.test(val)
                    ),
              }),

            longitude: yup
              .string()
              .nullable()
              .when(['hasExistingProject', 'addressInputType', 'showAddressInput'], {
                is: (hasExistingProject, type, show) =>
                  !hasExistingProject && type?.includes('coordinates') && show === true,
                then: () =>
                  yup
                    .string()
                    .required('Longitude is required')
                    .test('lngInRange', 'Longitude must be between -180 and 0', (val) =>
                      !projectOutsideUS ? Number(val) <= 0 && Number(val) >= -180 : true
                    )
                    .test(
                      'lngFormat',
                      'Longitude format is incorrect. Must be +-XXX.XXXXXX and include at least 6 decimal places.',
                      (val) => lngRegex.test(val)
                    ),
              }),

            projectArea: yup.string().when('hasExistingProject', {
              is: false,
              then: () =>
                yup
                  .string()
                  .matches(decimalNumberRegex, { message: 'Area is invalid', excludeEmptyString: true })
                  .required('Field is required'),
            }),

            unitOfMeasurement: yup.string().when('hasExistingProject', {
              is: false,
              then: () => yup.string().required('Please select an option'),
            }),

            parcelNumber: yup.string().nullable(),
            nearestWaterbody: yup.string().nullable(),
            fieldOfficeCode: yup.string().nullable(),
            siteDirections: yup.string().nullable(),
            plssSection: yup.string().nullable(),
            plssTownship: yup.string().nullable(),
            plssRange: yup.string().nullable(),
            plssRangeDir: yup.string().nullable(),
            plssTownshipDir: yup.string().nullable(),
            projectLocationGDBFile: yup.mixed().default([]),
          })
          .test('at-least-one-input', 'Please select a value for either Address, Map, or GDB input', function (value) {
            if (
              !value?.hasExistingProject &&
              !value?.showAddressInput &&
              !value?.showMapInput &&
              !value?.showGDBInput
            ) {
              return this.createError({
                path: 'inputType',
                message: 'Please select a value for either Address, Map, or GDB input',
              });
            }
            return true;
          })
          .test('geometry-required', 'Map: Please add a point, line or polygon to the map', function (value) {
            if (!value?.hasExistingProject && value?.showMapInput && (!value?.latitude || !value?.longitude)) {
              return this.createError({
                path: 'mapScroll',
                message: 'Map: Please add a point, line or polygon to the map',
              });
            }
            return true;
          })
          .test('file-selection-required', ErrorMessages.GDBFileMissing, function (value) {
            const files = value?.projectLocationGDBFile;
            const hasSelectedFile = Array.isArray(files) ? files.length > 0 : Boolean(files);
            if (!value?.hasExistingProject && value?.showGDBInput && !hasSelectedFile) {
              return this.createError({
                path: 'projectLocationGDBFile',
                message: ErrorMessages.GDBFileMissing,
              });
            }
            return true;
          }),
      [projectOutsideUS]
    );

    const methods = useForm({
      resolver: yupResolver(schema),
      mode: 'onBlur',
      defaultValues: defaultValues,
      shouldUnregister: true,
    });

    const { watch, handleSubmit, setValue, trigger } = methods;
    const requestType = watch('requestType');
    const state = watch('state');
    const hasExistingProject = watch('hasExistingProject');
    const existingProjectId = watch('existingProjectId');
    const latitude = watch('latitude');
    const longitude = watch('longitude');

    const { allowedRequestTypes } = useAllowedRequestTypesForLocation({
      requestLocationState: requestFormDataLocation?.state,
      requestLocationDistrict: requestFormDataLocation?.district,
      enabled: activeTab === 1,
    });

    const { data: existingProjectData } = useExistingProjects(!!hasExistingProject);

    const projectData = useMemo(() => {
      return existingProjectData?.find((p) => p.projectID === existingProjectId?.value);
    }, [existingProjectData, existingProjectId?.value]);

    const { data: selectedProjectData } = useProjectLocation({
      projectId: projectData?.projectID,
      projectVersion: !fromExistingProject ? projectData?.projectVersion : undefined,
    });
    const isExistingProject = Boolean(selectedProjectData?.projectName);

    const selectedRequestType = useMemo(() => {
      if (requestType?.includes?.('1') && requestType?.includes?.('2')) {
        return '1';
      } else {
        return requestType?.[0] || null;
      }
    }, [requestType]);

    const { data: orgData, isFetching: isOrgDataFetching } = useQuery({
      queryKey: [JPA_QUERY_KEYS.GET_ORG, state, district, requestFormDataLocation?.geometry, requestType],
      queryFn: async () => {
        const data = await JpaQueries.getOrgs({
          state: state || requestFormDataLocation?.state,
          district,
          geometry: requestFormDataLocation?.geometry,
          requestType: selectedRequestType,
        });
        const orgIds = data.map((org) => org?.jpaOrgID);
        setJpaOrgIds(orgIds);
        return data;
      },
      enabled: activeTab === 2,
    });

    const { mutate: addNpr } = useMutation({
      mutationFn: NprMutations.addNpr,
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD],
        });
        await navigate({
          to: '/forms',
        });
      },
    });

    const hasJpa =
      orgData?.filter((org) => org.validRequestTypes.includes(RequestTypeStringsLookup[selectedRequestType])).length >
      0;

    const handlePermitSetup = () => {
      doFetchGPPermitsByDistrict(district);
      prefetchCreditUnitsByDistrict(district);
    };

    useEffect(() => {
      doResetReduxFormData();
    }, [doResetReduxFormData]);

    useEffect(() => {
      latitude && trigger('latitude');
      longitude && trigger('longitude');
    }, [projectOutsideUS, trigger, latitude, longitude]);

    useEffect(() => {
      if (fromExistingProject && projectId) {
        const existingProjectId = {
          label: projectName,
          value: projectId,
          daNumber: daNumber,
        };
        setValue('hasExistingProject', true);
        setValue('existingProjectId', existingProjectId);
      }
    }, [fromExistingProject, projectId, projectName, daNumber, setValue]);

    useEffect(() => {
      const activeProject = selectedProjectData ?? projectData;
      if (!activeProject) return;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { requests: _, ...rest } = activeProject;
      doUpdateRequestData(rest);
    }, [selectedProjectData, projectData, doUpdateRequestData]);

    useEffect(() => {
      if (hasExistingProject) {
        methods.clearErrors('mapScroll');
      }
    }, [hasExistingProject, methods]);

    useEffect(() => {
      if (state && isExistingProject) {
        queryClient.invalidateQueries([JPA_QUERY_KEYS.GET_ORG]);
      }
    }, [state, district, requestFormDataLocation, queryClient, isExistingProject]);

    const onSave = (e) => {
      resetRequest();
      setIsNewRequest(true);

      const location = selectedProjectData?.location || {};

      const geometry = isExistingProject
        ? location?.geometry
        : e?.geometry?.type && e?.geometry?.coordinates
          ? e.geometry
          : projectGeometry || null;

      const locationProjectArea = location?.projectArea != null ? Number(location?.projectArea) : undefined;
      const eventProjectArea = e?.projectArea != null ? Number(e?.projectArea) : undefined;

      const locationValues = {
        latitude: formatCoordFlt(location?.latitude ?? e?.latitude),
        longitude: formatCoordFlt(location?.longitude ?? e?.longitude),
        address: location?.address || e?.address,
        addressTwo: location?.addressTwo || e?.addressTwo,
        city: location?.city || e?.city,
        state: location?.state || e?.state,
        zipcode: location?.zipcode || e?.zipcode,
        county: location?.county || e?.county,
        country: location?.country || e?.country,
        district: location?.district || district || e?.district,
        fieldOfficeCode: location?.fieldOfficeCode || e?.fieldOfficeCode,
        plssSection: plssData?.FRSTDIVNO,
        plssTownship: plssData?.TWNSHPNO,
        plssRange: plssData?.RANGENO,
        plssRangeDir: plssData?.RANGEDIR,
        plssTownshipDir: plssData?.TWNSHPDIR,
        projectArea: locationProjectArea ?? eventProjectArea,
        unitOfMeasurement: location?.unitOfMeasurement || e?.unitOfMeasurement,
        geometry,
      };

      const previousFileNumbers = buildPreviousFileNumbers(
        isExistingProject ? selectedProjectData?.previousFileNumbers : e?.previousFileNumbers,
        isExistingProject,
        selectedProjectData
      );

      const baseMappedValues = getBaseRequestValues({
        hasPreviousFileNumber: e?.hasPreviousFileNumber,
        location: { ...locationValues },
        previousFileNumbers: previousFileNumbers.length > 0 ? previousFileNumbers : undefined,
        projectName: isExistingProject ? selectedProjectData?.projectName : e?.projectName,
        projectID: isExistingProject ? selectedProjectData?.projectID : e?.projectID,
        projectDescription: isExistingProject ? selectedProjectData?.projectDescription : e?.projectDescription,
        natureOfActivity: isExistingProject ? selectedProjectData?.natureOfActivity : e?.natureOfActivity,
        projectPurpose: isExistingProject ? selectedProjectData?.projectPurpose : e?.projectPurpose,
        siteDirections: isExistingProject ? selectedProjectData?.siteDirections : e?.siteDirections,
        parcelNumber: isExistingProject ? selectedProjectData?.parcelNumber : e?.parcelNumber,
        nearestWaterbody: isExistingProject ? selectedProjectData?.nearestWaterbody : e?.nearestWaterbody,
        requestTypeId: selectedRequestType,
        projectVersion: isExistingProject ? selectedProjectData?.projectVersion : e?.projectVersion,
      });

      const updateRequestType =
        requestType?.length > 1
          ? RequestType.AquaticResourceDelineationReportAndJurisdictionalDetermination
          : selectedRequestType === '2'
            ? RequestType.JurisdictionalDetermination
            : RequestType.AquaticResourceDelineationReport;
      const isJdOrArRequest = selectedRequestType === '1' || selectedRequestType === '2';

      const jdMappedValues = {
        ...baseMappedValues,
        request: {
          requestType: updateRequestType,
          jdRequests: [{ requestType: updateRequestType }],
        },
      };

      const mappedValues = isJdOrArRequest ? jdMappedValues : baseMappedValues;

      const projectMappedValues = {
        project: mappedValues,
      };

      if (hasJpa) {
        projectMappedValues.jpaOrgIds = jpaOrgIds;
      }

      doUpdateRequestData(mappedValues);
      if (isJdOrArRequest) {
        doUpdateJDRequest({
          requestType: updateRequestType,
        });
      }

      switch (selectedRequestType) {
        case RequestTypeStringIds.AquaticResourceDelineationReport:
          setSelectedRequestId(RequestTypeStringIds.AquaticResourceDelineationReport);
          doAddJD(projectMappedValues);
          break;
        case RequestTypeStringIds.JurisdictionalDetermination:
          setSelectedRequestId(RequestTypeStringIds.JurisdictionalDetermination);
          doAddJD(projectMappedValues);
          break;
        case RequestTypeStringIds.ApplicationForDAPermit:
          setSelectedRequestId(RequestTypeStringIds.ApplicationForDAPermit);
          doAddPermit(projectMappedValues);
          handlePermitSetup();
          break;
        case RequestTypeStringIds.PreconstructionNotification:
          setSelectedRequestId(RequestTypeStringIds.PreconstructionNotification);
          doAddPermit(projectMappedValues);
          handlePermitSetup();
          break;
        case RequestTypeStringIds.PreApplication:
          setSelectedRequestId(RequestTypeStringIds.PreApplication);
          doAddPreApp(projectMappedValues);
          break;
        case RequestTypeStringIds.NoPermitRequired:
          setSelectedRequestId(RequestTypeStringIds.NoPermitRequired);
          addNpr(projectMappedValues);
          break;
        case RequestTypeStringIds.NonPreconstructionNotification:
          setSelectedRequestId(RequestTypeStringIds.NonPreconstructionNotification);
          addNonPcn(projectMappedValues);
          break;
        case RequestTypeStringIds.ActivityBasedNoPermitRequired:
          setSelectedRequestId(RequestTypeStringIds.ActivityBasedNoPermitRequired);
          addNpr(projectMappedValues);
          break;
        case RequestTypeStringIds.PermitApplication:
          setSelectedRequestId(RequestTypeStringIds.PermitApplication);
          doAddPermit(projectMappedValues);
          handlePermitSetup();
          break;
        default:
          console.error('INVALID ENDPOINT');
          break;
      }
      queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEYS.GET_EXISTING_PROJECT] });
      closeModal();
    };

    const tabs = [
      {
        content: (
          <ProjectLocationTab
            activeTab={activeTab}
            setProjectOutsideUS={setProjectOutsideUS}
            projectOutsideUS={projectOutsideUS}
            fromExistingProject={fromExistingProject}
          />
        ),
        setDisabled:
          hasExistingProject === null ||
          (!hasExistingProject && !districtLocations?.length) ||
          (hasExistingProject && !existingProjectId),
      },
      {
        content: <RequestTypes district={district} allowedRequestTypes={allowedRequestTypes} />,
        setDisabled: requestType?.length === 0,
      },
      {
        content: (
          <ProjectConfirmationTab activeTab={activeTab} hasJpa={hasJpa} orgData={orgData} requestType={requestType} />
        ),
        setDisabled: false,
        setIsLoading: isOrgDataFetching,
      },
    ];

    return (
      <ModalContent
        className={hasExistingProject === false || isExistingProject ? 'modal-resize' : 'modal-base'}
        title='Start a New Request'
      >
        <FormProvider {...methods}>
          <ModalTabContainerNewRequest
            contentClassName={hasExistingProject ? 'expanded-content' : ''}
            onSave={handleSubmit(onSave)}
            projectData={selectedProjectData}
            setActiveTab={setActiveTab}
            tabs={tabs}
            requestFormDataLocation={requestFormDataLocation}
          />
        </FormProvider>
      </ModalContent>
    );
  }
);

export default NewApplicationModal;
